import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { AnimatePresence, m } from "framer-motion";
import { useEffect } from "react";
import { easeOut } from "./motion/tokens";

export type LightboxImage = { src: string; alt?: string };

type LightboxProps = {
  images: LightboxImage[];
  index: number;
  open: boolean;
  title?: string;
  onClose: () => void;
  onIndexChange: (index: number) => void;
};

/**
 * Shared full-screen photo viewer (factored from the apartment-detail gallery).
 * Reuses the `.apartment-viewer` styling, adds AnimatePresence open/close, and
 * handles Esc / arrow keys + body-scroll lock. Used by Gallery and ApartmentDetail.
 */
export function Lightbox({ images, index, open, title, onClose, onIndexChange }: LightboxProps) {
  const count = images.length;
  const safeIndex = count ? ((index % count) + count) % count : 0;
  const current = images[safeIndex];

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "ArrowLeft" && count > 1) {
        onIndexChange(safeIndex === 0 ? count - 1 : safeIndex - 1);
      } else if (event.key === "ArrowRight" && count > 1) {
        onIndexChange((safeIndex + 1) % count);
      }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, count, safeIndex, onClose, onIndexChange]);

  return (
    <AnimatePresence>
      {open && current && (
        <m.div
          className="apartment-viewer"
          role="dialog"
          aria-modal="true"
          aria-label={`${title ?? "Photo"} viewer`}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
        >
          <m.div
            className="apartment-viewer__chrome"
            onClick={(event) => event.stopPropagation()}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.28, ease: easeOut }}
          >
            <div className="apartment-viewer__topbar">
              <span>
                {title ? `${title} / ` : ""}
                {safeIndex + 1} of {count}
              </span>
              <button className="icon-button apartment-viewer__close" type="button" onClick={onClose} aria-label="Close photo viewer">
                <X size={22} aria-hidden="true" />
              </button>
            </div>
            <div className="apartment-viewer__stage">
              {count > 1 && (
                <button
                  className="icon-button apartment-viewer__nav apartment-viewer__nav--prev"
                  type="button"
                  onClick={() => onIndexChange(safeIndex === 0 ? count - 1 : safeIndex - 1)}
                  aria-label="Previous photo"
                >
                  <ChevronLeft size={28} aria-hidden="true" />
                </button>
              )}
              <img src={current.src} alt={current.alt ?? `Photo ${safeIndex + 1}`} />
              {count > 1 && (
                <button
                  className="icon-button apartment-viewer__nav apartment-viewer__nav--next"
                  type="button"
                  onClick={() => onIndexChange((safeIndex + 1) % count)}
                  aria-label="Next photo"
                >
                  <ChevronRight size={28} aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="apartment-viewer__thumbs" aria-label="Photo thumbnails">
              {images.map((image, i) => (
                <button
                  className={`apartment-viewer__thumb ${i === safeIndex ? "apartment-viewer__thumb--active" : ""}`}
                  key={`${image.src}-thumb-${i}`}
                  type="button"
                  onClick={() => onIndexChange(i)}
                  aria-label={`Show photo ${i + 1}`}
                >
                  <img src={image.src} alt="" />
                </button>
              ))}
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
